/**
 * Phase 2c — Live docs search bar.
 *
 * Replaces the placeholder Cmd-K trigger button at the top of the
 * docs main column with a real `<input>` that hits /api/wiki/search
 * as the user types and renders a dropdown of matching pages
 * (title + excerpt + tags) deep-linking into the article.
 *
 * Behaviour:
 *   - Debounced 180ms (avoid hammering the search endpoint mid-keystroke)
 *   - Min 2 chars before a query fires
 *   - ↓/↑ to move highlight, Enter to navigate, Esc to close
 *   - Clicking outside closes
 *   - "/" keyboard shortcut focuses the bar (same as GitHub, Vercel docs)
 *   - Cmd+K still opens the modal palette (preserved for power users)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight, FileText } from "lucide-react";


const API = process.env.REACT_APP_BACKEND_URL;
// Iter269.4 — perf parity with the top-header search.
//   - 120ms debounce (down from 180) so the dropdown feels instant.
//   - Client-side memo of the last N (q → results) pairs so backspacing
//     to a previous query is zero-latency.
//   - Optimistically keep the old results visible while the new query
//     is in flight, so the dropdown never goes empty mid-typing.
const DEBOUNCE_MS = 120;
const MIN_QUERY = 2;
const CACHE_MAX = 32;
const _resultCache = new Map();  // q-lower → { results, source, at }


export default function DocsSearchBar({ onOpenPalette }) {
  const [q, setQ]               = useState("");
  const [results, setResults]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [highlight, setHl]      = useState(0);
  const navigate                = useNavigate();
  const wrapRef                 = useRef(null);
  const inputRef                = useRef(null);
  const debounceRef             = useRef(0);
  const reqTokenRef             = useRef(0);

  // Debounced search. Iter269.4 — checks the in-memory cache first so
  // backspacing or re-typing the same prefix returns instantly.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = q.trim();
    if (trimmed.length < MIN_QUERY) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    // Cache hit → instant render.
    const key = trimmed.toLowerCase();
    const cached = _resultCache.get(key);
    if (cached && (Date.now() - cached.at) < 30_000) {
      setResults(cached.results);
      setHl(0);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const myToken = ++reqTokenRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/wiki/search`,
                                  { params: { q: trimmed }, withCredentials: false });
        // Drop stale responses if a newer query has fired since.
        if (myToken !== reqTokenRef.current) return;
        const rows = r.data?.results || [];
        // LRU-ish: drop oldest if we'd exceed cap.
        if (_resultCache.size >= CACHE_MAX) {
          const first = _resultCache.keys().next().value;
          if (first) _resultCache.delete(first);
        }
        _resultCache.set(key, { results: rows, source: r.data?.source, at: Date.now() });
        setResults(rows);
        setHl(0);
      } catch {
        if (myToken === reqTokenRef.current) setResults([]);
      } finally {
        if (myToken === reqTokenRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Outside-click closes the dropdown.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // "/" anywhere on the page focuses the search bar (skip when typing in
  // another input). Standard pattern from GitHub/Vercel docs.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goto = useCallback(
    (slug, rank = 0) => {
      if (!slug) return;
      // Fire-and-forget click telemetry. Never block navigation.
      try {
        const trimmed = q.trim();
        if (trimmed) {
          axios.post(
            `${API}/api/wiki/search/click`,
            { q: trimmed, slug, rank },
            { withCredentials: false },
          ).catch(() => {});
        }
      } catch { /* noop */ }
      setOpen(false);
      setQ("");
      navigate(`/docs/${slug}`);
    },
    [navigate, q],
  );

  const onKeyDown = (e) => {
    if (!open && results.length > 0 && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHl((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHl((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      const r = results[highlight];
      if (r) {
        e.preventDefault();
        goto(r.slug, highlight);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const showDropdown = open && q.trim().length >= MIN_QUERY;
  const placeholder = useMemo(
    () => "Search the docs… (press / to focus, ⌘K for full palette)",
    [],
  );

  return (
    <div className="qt-docs-searchbar-wrap" ref={wrapRef} data-testid="docs-search-bar-wrap">
      <div className={`qt-docs-searchbar qt-docs-searchbar-input ${showDropdown ? "is-open" : ""}`}>
        <Search size={14} className="qt-docs-searchbar-icon" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label="Search the docs"
          role="combobox"
          aria-autocomplete="list"
          {...(showDropdown ? { "aria-controls": "docs-search-results" } : {})}
          aria-expanded={showDropdown}
          autoComplete="off"
          spellCheck="false"
          data-testid="docs-search-input"
        />
        {q && (
          <button
            type="button"
            className="qt-docs-searchbar-clear"
            onClick={() => { setQ(""); setResults([]); inputRef.current?.focus(); }}
            aria-label="Clear search"
            data-testid="docs-search-clear"
          >
            ×
          </button>
        )}
        <button
          type="button"
          className="qt-docs-searchbar-kbd qt-docs-searchbar-kbd-btn"
          onClick={onOpenPalette}
          title="Open full search palette"
          aria-label="Open full search palette"
          data-testid="docs-search-open-palette"
        >
          ⌘K
        </button>
      </div>

      {showDropdown && (
        <div
          id="docs-search-results"
          className="qt-docs-search-dropdown"
          role="listbox"
          aria-label="Search results"
          data-testid="docs-search-results"
        >
          {loading && results.length === 0 && (
            <div className="qt-docs-search-status">Searching…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="qt-docs-search-status">No matches for &ldquo;{q}&rdquo;.</div>
          )}
          {results.map((r, i) => (
            <button
              key={r.slug}
              type="button"
              role="option"
              aria-selected={i === highlight}
              className={`qt-docs-search-result ${i === highlight ? "is-highlighted" : ""}`}
              onMouseEnter={() => setHl(i)}
              onClick={() => goto(r.slug, i)}
              data-testid={`docs-search-result-${r.slug.replace(/\//g, "-")}`}
            >
              <div className="qt-docs-search-result-head">
                <FileText size={11} className="qt-docs-search-result-icon" />
                <span className="qt-docs-search-result-title">
                  <Highlighted text={r.title} q={q} />
                </span>
                <span className="qt-docs-search-result-slug">/docs/{r.slug}</span>
                <ArrowRight size={11} className="qt-docs-search-result-arrow" />
              </div>
              {(r.body_excerpt || r.summary) && (
                <div className="qt-docs-search-result-excerpt">
                  <Highlighted text={r.body_excerpt || r.summary} q={q} />
                </div>
              )}
              {r.tags?.length > 0 && (
                <div className="qt-docs-search-result-tags">
                  {r.tags.slice(0, 4).map((t) => (
                    <span key={t} className="qt-docs-search-result-tag">{t}</span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


/** Marks every case-insensitive substring match of `q` inside `text` with a
 * <mark> for the cyan highlight class — purely visual, no security risk
 * because we render text only via React (not innerHTML). */
function Highlighted({ text, q }) {
  if (!text || !q) return text || null;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="qt-docs-search-mark">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}
